// Operator cross-tenant org reads — mirror OperatorOrgService / GET /operator/orgs
// + /operator/metrics. Success bodies are Zod-validated (ContractError on drift).
import { z } from "zod";
import { operatorFetch } from "./client";
import { agentsResponseSchema, type AgentSummary } from "../agents";

export type OrgTier = "free" | "small" | "medium" | "large" | "xlarge";
export type OrgStatus = "active" | "suspended" | "deleted";

export interface OperatorOrgSummary {
  id: string;
  name: string;
  tier: OrgTier;
  status: OrgStatus;
  subscriptionStatus: string | null;
  currentPeriodEnd: string | null;
  channels: number;
  members: number;
  createdAt: string;
}

const orgSummarySchema = z.object({
  id: z.string(),
  name: z.string(),
  tier: z.enum(["free", "small", "medium", "large", "xlarge"]),
  status: z.enum(["active", "suspended", "deleted"]),
  subscriptionStatus: z.string().nullable(),
  currentPeriodEnd: z.string().nullable(),
  channels: z.number(),
  members: z.number(),
  createdAt: z.string(),
}) satisfies z.ZodType<OperatorOrgSummary>;

export interface OperatorOrgPage {
  orgs: OperatorOrgSummary[];
  nextCursor: string | null;
}

const orgPageSchema = z.object({
  orgs: z.array(orgSummarySchema),
  nextCursor: z.string().nullable(),
}) satisfies z.ZodType<OperatorOrgPage>;

/** Subscription-state buckets the org list can filter by (`none` = no subscription). */
export type SubscriptionFilter = "active" | "trialing" | "past_due" | "canceled" | "none";

export interface ListOrgsOptions {
  /** Searches name / org_scope / stripe id / any admin email (server-side). */
  q?: string;
  before?: string;
  limit?: number;
  status?: OrgStatus;
  tier?: OrgTier;
  subscription?: SubscriptionFilter;
}

export function listOperatorOrgs(opts: ListOrgsOptions = {}, signal?: AbortSignal): Promise<OperatorOrgPage> {
  const p = new URLSearchParams();
  if (opts.q) p.set("q", opts.q);
  if (opts.before) p.set("before", opts.before);
  if (opts.limit) p.set("limit", String(opts.limit));
  if (opts.status) p.set("status", opts.status);
  if (opts.tier) p.set("tier", opts.tier);
  if (opts.subscription) p.set("subscription", opts.subscription);
  const qs = p.toString();
  return operatorFetch(`/operator/orgs${qs ? `?${qs}` : ""}`, { signal, schema: orgPageSchema });
}

export interface OperatorMetrics {
  orgs: { total: number; active: number; suspended: number; deleted: number };
  byTier: Record<OrgTier, number>;
  channels: number;
  members: number;
  activeSubscriptions: number;
  /** Estimated MRR (tier list price × active subscriptions), in GBP. */
  mrrGbp: number;
}

const metricsSchema = z.object({
  orgs: z.object({ total: z.number(), active: z.number(), suspended: z.number(), deleted: z.number() }),
  byTier: z.object({ free: z.number(), small: z.number(), medium: z.number(), large: z.number(), xlarge: z.number() }),
  channels: z.number(),
  members: z.number(),
  activeSubscriptions: z.number(),
  mrrGbp: z.number(),
}) satisfies z.ZodType<OperatorMetrics>;

export function getOperatorMetrics(signal?: AbortSignal): Promise<OperatorMetrics> {
  return operatorFetch("/operator/metrics", { signal, schema: metricsSchema });
}

/** An org's live per-agent metadata (operator god-mode). Degrades to [] server-side. */
export async function listOperatorOrgAgents(id: string, signal?: AbortSignal): Promise<AgentSummary[]> {
  const res = await operatorFetch(`/operator/orgs/${encodeURIComponent(id)}/agents`, { signal, schema: agentsResponseSchema });
  return res.agents;
}

// --- Channels behind an org (operator drill-down) ---------------------------

/** Live relay stats for a channel (metadata only); `null` when the channel has no
 *  live relay session (idle) or the relay link is off. */
export interface OperatorChannelLive {
  peerCount: number;
  agentCount: number;
  messageCount: number;
  lastActivityAt: string | null;
}

export interface OperatorChannel {
  id: string;
  name: string;
  relaySessionId: string;
  createdAt: string;
  /** Count of ACTIVE Slack channel mappings bound to this channel. */
  slackMappings: number;
  live: OperatorChannelLive | null;
}

const channelLiveSchema = z.object({
  peerCount: z.number(),
  agentCount: z.number(),
  messageCount: z.number(),
  lastActivityAt: z.string().nullable(),
}) satisfies z.ZodType<OperatorChannelLive>;

const operatorChannelSchema = z.object({
  id: z.string(),
  name: z.string(),
  relaySessionId: z.string(),
  createdAt: z.string(),
  slackMappings: z.number(),
  live: channelLiveSchema.nullable(),
}) satisfies z.ZodType<OperatorChannel>;

const channelsResponseSchema = z.object({ channels: z.array(operatorChannelSchema) });

/** The channels behind an org (Tier A rows + Tier B live stats). Degrades to idle server-side. */
export async function listOperatorOrgChannels(id: string, signal?: AbortSignal): Promise<OperatorChannel[]> {
  const res = await operatorFetch(`/operator/orgs/${encodeURIComponent(id)}/channels`, { signal, schema: channelsResponseSchema });
  return res.channels;
}

// --- Org detail + mutations -------------------------------------------------

export interface OperatorOrgAdmin {
  id: string;
  email: string;
  name: string | null;
  role: "owner" | "admin" | "member";
  createdAt: string;
}

export interface OperatorOrgDetail extends OperatorOrgSummary {
  /** SHA-256(Org_ID) — relay correlation key (never the Org_ID itself). */
  orgScope: string;
  stripeCustomerId: string | null;
  updatedAt: string;
  /** True when an operator set a durable tier override (billing won't reconcile the tier). */
  tierOverride: boolean;
  /** Tier-derived channel ceiling (for the quota gauge). */
  channelLimit: number;
  /** Tier-derived agent ceiling (for the quota gauge). */
  agentLimit: number;
  admins: OperatorOrgAdmin[];
}

const orgDetailSchema = orgSummarySchema.extend({
  orgScope: z.string(),
  stripeCustomerId: z.string().nullable(),
  updatedAt: z.string(),
  tierOverride: z.boolean(),
  channelLimit: z.number(),
  agentLimit: z.number(),
  admins: z.array(
    z.object({
      id: z.string(),
      email: z.string(),
      name: z.string().nullable(),
      role: z.enum(["owner", "admin", "member"]),
      createdAt: z.string(),
    }),
  ),
}) satisfies z.ZodType<OperatorOrgDetail>;

export function getOperatorOrg(id: string, signal?: AbortSignal): Promise<OperatorOrgDetail> {
  return operatorFetch(`/operator/orgs/${encodeURIComponent(id)}`, { signal, schema: orgDetailSchema });
}

/** Suspend or reactivate an org; returns the refreshed detail. */
export function setOrgStatus(id: string, status: "active" | "suspended"): Promise<OperatorOrgDetail> {
  return operatorFetch(`/operator/orgs/${encodeURIComponent(id)}/status`, { method: "PATCH", body: { status }, schema: orgDetailSchema });
}

/** Manually change an org's tier (operator promo/comp). `note` is required; returns refreshed detail. */
export function setOrgTier(id: string, input: { tier: OrgTier; note: string }): Promise<OperatorOrgDetail> {
  return operatorFetch(`/operator/orgs/${encodeURIComponent(id)}/tier`, { method: "PATCH", body: input, schema: orgDetailSchema });
}

export interface RefundResult {
  refundId: string;
  amount: number;
  currency: string;
  status: string | null;
}
const refundResultSchema = z.object({
  refundId: z.string(),
  amount: z.number(),
  currency: z.string(),
  status: z.string().nullable(),
}) satisfies z.ZodType<RefundResult>;

/** Refund a charge for an org. `amount` (optional) is a partial refund in MINOR units. */
export function refundCharge(id: string, input: { chargeId: string; amount?: number }): Promise<RefundResult> {
  return operatorFetch(`/operator/orgs/${encodeURIComponent(id)}/refund`, { method: "POST", body: input, schema: refundResultSchema });
}

export interface CancelSubscriptionResult {
  subscriptionId: string;
  status: string;
  cancelAtPeriodEnd: boolean;
  currentPeriodEnd: string | null;
}
const cancelSubscriptionResultSchema = z.object({
  subscriptionId: z.string(),
  status: z.string(),
  cancelAtPeriodEnd: z.boolean(),
  currentPeriodEnd: z.string().nullable(),
}) satisfies z.ZodType<CancelSubscriptionResult>;

/** Cancel an org's subscription at period end (operator). `note` is required (saved for reconciliation). */
export function cancelSubscription(id: string, input: { note: string }): Promise<CancelSubscriptionResult> {
  return operatorFetch(`/operator/orgs/${encodeURIComponent(id)}/cancel-subscription`, { method: "POST", body: input, schema: cancelSubscriptionResultSchema });
}

/** Release an org's tier back to Stripe billing control (clears the operator override). Note required. */
export function releaseTierToBilling(id: string, input: { note: string }): Promise<OperatorOrgDetail> {
  return operatorFetch(`/operator/orgs/${encodeURIComponent(id)}/release-tier`, { method: "POST", body: input, schema: orgDetailSchema });
}

export interface ImpersonationResult {
  token: string;
  expiresInSeconds: number;
  admin: { id: string; orgId: string; email: string };
}
const impersonationSchema = z.object({
  token: z.string().min(1),
  expiresInSeconds: z.number(),
  admin: z.object({ id: z.string(), orgId: z.string(), email: z.string() }),
}) satisfies z.ZodType<ImpersonationResult>;

/** Mint a short-lived support-impersonation token for the org (as its owner). */
export function impersonateOrg(id: string): Promise<ImpersonationResult> {
  return operatorFetch(`/operator/orgs/${encodeURIComponent(id)}/impersonate`, { method: "POST", schema: impersonationSchema });
}
